import React, { useEffect, useState } from "react";
import {
  FaTruck,
  FaBoxes,
  FaClipboardCheck,
  FaWarehouse,
  FaChartBar,
  FaDollarSign,
  FaFileInvoice,
  FaLink,
  FaCubes,
  FaShippingFast,
  FaCheckCircle,
  FaHourglassHalf,
  FaUserTie,
  FaExclamationTriangle,
} from "react-icons/fa";
import { MdInventory, MdLocalShipping } from "react-icons/md";
import { toast } from "react-toastify";
import { useAuth } from "../../context/Auth";
import { useGlobalContext } from "../../context/stateContext";
import { useNavigate } from "react-router-dom";
import ExpiryAlertModal from "../../components/modals/ExpiryAlertModal";

const WarehouseManagerDashboard = () => {
  const navigate = useNavigate();
  const { requestWithAuth } = useAuth();
  const { activeSession } = useGlobalContext();
  const [gettingDashboardData, setGettingDashboardData] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [showExpiryAlert, setShowExpiryAlert] = useState(false);

  // Check if alert should be shown
  useEffect(() => {
    const checkExpiryAlert = () => {
      const today = new Date().toISOString().split("T")[0];
      const dismissedDate = localStorage.getItem(
        "warehouseExpiryAlertDismissed"
      );
      const hasExpiryIssues =
        dashboardData?.expiring_drugs > 0 || dashboardData?.expired_drugs > 0;
      const shouldShow = hasExpiryIssues && dismissedDate !== today;

      if (shouldShow) {
        setTimeout(() => {
          setShowExpiryAlert(true);
        }, 500);
      }
    };

    if (!gettingDashboardData && dashboardData) {
      checkExpiryAlert();
    }
  }, [
    gettingDashboardData,
    dashboardData?.expiring_drugs,
    dashboardData?.expired_drugs,
  ]);

  const handleCloseExpiryAlert = () => {
    setShowExpiryAlert(false);
  };

  const handleCloseAndHideExpiryAlert = () => {
    if (dashboardData?.expired_drugs > 0) {
      toast.error(
        `You have ${dashboardData?.expired_drugs} expired drugs, please clear them before continuing`
      );
      return;
    }
    const today = new Date().toISOString().split("T")[0];
    localStorage.setItem("warehouseExpiryAlertDismissed", today);
    setShowExpiryAlert(false);
  };

  const handleViewExpiringDrugs = () => {
    const branch_id = activeSession?.branch?.id;
    setShowExpiryAlert(false);
    navigate(`/inventory/branch/expiring-drugs/${branch_id}`);
  };

  const getWarehouseDashboardData = async () => {
    setGettingDashboardData(true);
    try {
      const response = await requestWithAuth("/users/warehouse/dashboard");
      setDashboardData(response);
    } catch (error) {
      toast.error("Failed to get dashboard data");
    } finally {
      setGettingDashboardData(false);
    }
  };

  useEffect(() => {
    getWarehouseDashboardData();
  }, []);

  const StatCard = ({
    title,
    value,
    subtitle,
    additionalInfo,
    icon: Icon,
    color,
    linkTo,
  }) => (
    <div
      className={`rounded-lg p-6 ${color} text-white shadow-lg hover:shadow-xl transition-shadow`}
    >
      <div className="flex items-center justify-between mb-4">
        <Icon className="text-2xl opacity-80" />
        <div className="text-right">
          <h3 className="text-2xl font-bold">{value}</h3>
          <p className="text-sm opacity-90">{title}</p>
        </div>
      </div>
      {subtitle && <p className="text-xs opacity-75 mb-2">{subtitle}</p>}
      {additionalInfo && (
        <p className="text-xs opacity-75 mb-3">{additionalInfo}</p>
      )}
      {linkTo && (
        <button
          onClick={() => navigate(linkTo)}
          className="inline-flex items-center gap-1 text-sm hover:underline cursor-pointer"
        >
          Manage <FaLink className="text-[10px]" />
        </button>
      )}
    </div>
  );

  const SimpleCard = ({
    title,
    value,
    linkTo,
    color = "bg-gray-100",
    subtitle,
    icon: Icon,
  }) => (
    <div
      className={`rounded-lg p-6 ${color} text-center shadow-md hover:shadow-lg transition-shadow`}
    >
      <div className="flex justify-center mb-3">
        {Icon && <Icon className="text-3xl text-gray-600" />}
      </div>
      <h3 className="text-2xl font-bold mb-2 text-gray-800">{value}</h3>
      <p className="text-sm text-gray-600 mb-3">{title}</p>
      {subtitle && <p className="text-xs text-gray-500 mb-3">{subtitle}</p>}
      {linkTo && (
        <button
          onClick={() => navigate(linkTo)}
          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline cursor-pointer"
        >
          Access <FaLink className="text-[12px]" />
        </button>
      )}
    </div>
  );

  if (gettingDashboardData) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
            <FaWarehouse className="text-indigo-600" />
            Warehouse Manager Dashboard
          </h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="bg-gray-200 rounded-lg h-36 animate-pulse"
            ></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto min-h-screen">
      <ExpiryAlertModal
        isOpen={showExpiryAlert}
        onClose={handleCloseExpiryAlert}
        onHideModal={handleCloseAndHideExpiryAlert}
        expiringDrugs={dashboardData?.expiring_drugs || 0}
        expiredDrugs={dashboardData?.expired_drugs || 0}
        onViewDetails={handleViewExpiringDrugs}
      />

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
          <FaWarehouse className="text-indigo-600" />
          Warehouse Manager Dashboard
        </h1>
        <p className="text-gray-600">
          Warehouse operations control center - Supply chain & inventory
          management
        </p>
      </div>

      {/* Supply & Requisition Management */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2 flex items-center gap-2">
          <FaTruck className="text-gray-600" />
          Supply & Requisition Management
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Inventory supplies and branch requisition processing
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Pending Branch Requisitions"
          value={dashboardData?.pending_requisitions || "0"}
          additionalInfo="Authorize branch requests"
          icon={FaClipboardCheck}
          color="bg-green-600"
          linkTo="/authorize/requisition"
        />

        <StatCard
          title="Expiry Alert"
          value={dashboardData?.expiring_drugs || "0"}
          subtitle="Items that are about to expire"
          icon={FaExclamationTriangle}
          color="bg-red-600"
          linkTo={`/inventory/branch/expiring-drugs/${activeSession?.branch?.id}`}
        />

        <StatCard
          title="Expired Drugs"
          value={dashboardData?.expired_drugs || "0"}
          subtitle="Items already expired"
          additionalInfo="Clear expired stock"
          icon={FaExclamationTriangle}
          color="bg-orange-600"
          linkTo={`/inventory/branch/expiring-drugs/${activeSession?.branch?.id}`}
        />

        <StatCard
          title="Pending Store Supplies"
          value={dashboardData?.pending_warehouse_requisitions || "0"}
          subtitle="Supplies for stores pending approval"
          additionalInfo="Approve supplies for stores"
          icon={FaShippingFast}
          color="bg-purple-600"
          linkTo="/warehouse-manager/inventory/requisition"
        />

        {/* <StatCard
          title="Batch Entries"
          value={dashboardData?.active_batches || "156"}
          subtitle={`${dashboardData?.expiring_soon || "5"} expiring soon`}
          additionalInfo="Create & manage batches"
          icon={FaCubes}
          color="bg-orange-600"
          linkTo="/warehouse/batches"
        /> */}
      </div>

      {/* Inventory & Pricing Control */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Inventory & Pricing Control
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Warehouse inventory management and invoice pricing
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <SimpleCard
          title="Invoice Pricing"
          value="Manage"
          subtitle="Set and update invoice pricing"
          linkTo="/administration/inventory/"
          color="bg-white border-2 border-blue-200 text-blue-800"
          icon={FaFileInvoice}
        />

        <SimpleCard
          title="Warehouse Inventory"
          value={`${dashboardData?.warehouse_stock_items || "0"} Items`}
          linkTo="/inventory/reorder"
          color="bg-white border-2 border-green-200 text-green-800"
          icon={MdInventory}
        />

        <SimpleCard
          title="Low Stock Items"
          value={dashboardData?.items_below_reorder || "0"}
          subtitle="Below reorder level"
          linkTo="/inventory/reorder/level"
          color="bg-white border-2 border-red-200 text-red-800"
          icon={FaBoxes}
        />
      </div>
    </div>
  );
};

export default WarehouseManagerDashboard;
